/**
 * Hook pour intégrer les sessions partagées dans l'application VisualGrid
 * Gère la synchronisation bidirectionnelle entre appareils
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { SessionInfo, SessionManager, SessionEventCallbacks } from '@/lib/sessions/types';
import { toast } from 'sonner';
import { useLanguage } from '@/lib/i18n/language-provider';

interface SharedSessionHookResult {
  // État de la session
  activeSession: SessionInfo | null;
  isSessionActive: boolean;
  isSessionServer: boolean;
  isSessionRemote: boolean;
  sessionManager: SessionManager | null;

  // Configuration synchronisée
  syncedConfig: any;
  
  // Appareils connectés
  connectedDevices: any[];
  isDeviceConnected: boolean;
  
  // Actions
  updateConfig: (config: any) => Promise<void>;
  sendSyncMessage: (type: string, data: any) => Promise<void>;
  endSession: () => Promise<void>;
  
  // Callbacks pour l'intégration
  setConfigUpdateCallback: (callback: (config: any) => void) => void;
}

interface UseSharedSessionOptions {
  // Callbacks automatiques
  onConfigReceived?: (config: any) => void;
  onAnimationStateReceived?: (isAnimating: boolean) => void;
  onSessionEnd?: () => void;
  onDeviceJoined?: (device: any) => void;
  onDeviceLeft?: (deviceId: string) => void;
  
  // Options de synchronisation
  autoSyncConfig?: boolean;
  debounceMs?: number;
}

export function useSharedSession(options: UseSharedSessionOptions = {}): SharedSessionHookResult {
  
  const { t } = useLanguage();
  
  const [activeSession, setActiveSession] = useState<SessionInfo | null>(null);
  const [sessionManager, setSessionManager] = useState<SessionManager | null>(null);
  const [syncedConfig, setSyncedConfig] = useState<any>(null);
  const [connectedDevices, setConnectedDevices] = useState<any[]>([]);
  const [isDeviceConnected, setIsDeviceConnected] = useState(false);
  
  const configUpdateCallbackRef = useRef<((config: any) => void) | null>(null);
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  const {
    onConfigReceived,
    onAnimationStateReceived,
    onSessionEnd,
    onDeviceJoined,
    onDeviceLeft,
    autoSyncConfig = true,
    debounceMs = 300
  } = options;

  // État dérivé
  const isSessionActive = activeSession?.status === 'active';
  const isSessionServer = activeSession?.role === 'server';
  const isSessionRemote = activeSession?.role === 'remote';

  // Initialiser une session (appelé depuis SessionCreator)
  const initializeSession = useCallback((sessionInfo: SessionInfo, manager: SessionManager) => {
    console.log('🎯 [SHARED_SESSION] Initialisation session:', sessionInfo.code);
    
    setActiveSession(sessionInfo);
    setSessionManager(manager);

    // Configurer les callbacks du manager
    manager.setCallbacks({
      onConfigUpdate: (config) => {
        console.log('📥 [SHARED_SESSION] Config reçue:', Object.keys(config));
        
        setSyncedConfig(config);
        
        // Notifier le callback externe si configuré
        if (onConfigReceived) {
          onConfigReceived(config);
        }
        
        // Notifier le callback personnalisé
        if (configUpdateCallbackRef.current) {
          configUpdateCallbackRef.current(config);
        }
      },
      
      onAnimationStateUpdate: (isAnimating) => {
        console.log('🎬 [SHARED_SESSION] État animation reçu:', isAnimating);
        
        // Notifier le callback externe si configuré
        if (onAnimationStateReceived) {
          onAnimationStateReceived(isAnimating);
        }
      },
      
      onDeviceJoin: (device) => {
        console.log('👋 [SHARED_SESSION] Appareil rejoint:', device.name);
        setConnectedDevices(prev => {
          if (prev.some(d => d.id === device.id)) return prev;
          const updated = [...prev, device];
          setIsDeviceConnected(true);
          return updated;
        });
        onDeviceJoined?.(device);
      },
      
      onDeviceLeave: (deviceId) => {
        console.log('👋 [SHARED_SESSION] Appareil parti:', deviceId);
        setConnectedDevices(prev => {
          console.log('🔍 [SHARED_SESSION] Avant suppression:', prev.map(d => d.id));
          const updated = prev.filter(d => d.id !== deviceId);
          console.log('🔍 [SHARED_SESSION] Après suppression:', updated.map(d => d.id));
          setIsDeviceConnected(updated.length > 0);
          return updated;
        });
        onDeviceLeft?.(deviceId);
      },
      
      onSessionTerminated: () => {
        console.log('🔚 [SHARED_SESSION] Session terminée');
        handleSessionEnd();
        onSessionEnd?.();
      },
      
      onConnectionLost: () => {
        toast.error(t('sharedSession.connectionLost'));
      },
      
      onConnectionRestored: () => {
        toast.success(t('sharedSession.connectionRestored'));
      },
      
      onError: (error) => {
        toast.error(t("toast.sessionError", { error }));
      }
    });

  }, [onConfigReceived, onSessionEnd, onDeviceJoined, onDeviceLeft, toast]);

  // Mettre à jour la configuration (avec debounce)
  const updateConfig = useCallback(async (config: any) => {
    if (!sessionManager || !isSessionActive || !isSessionRemote) {
      return;
    }

    // Vérifier que le manager est toujours connecté
    if (!sessionManager.isConnected()) {
      console.log('⚠️ [SHARED_SESSION] Session déconnectée, config ignorée');
      return;
    }

    // Debounce pour éviter trop de messages
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    debounceTimeoutRef.current = setTimeout(async () => {
      // Re-vérifier la connexion après le debounce
      if (!sessionManager.isConnected()) {
        console.log('⚠️ [SHARED_SESSION] Session déconnectée pendant debounce');
        return;
      }
      try {
        console.log('📤 [SHARED_SESSION] Envoi config:', Object.keys(config));
        await sessionManager.syncConfig(config);
        setSyncedConfig(config);
      } catch (error: any) {
        // Ne pas afficher d'erreur si la session est fermée
        if (error?.message?.includes('non connectée')) {
          console.log('⚠️ [SHARED_SESSION] Session fermée, config non envoyée');
        } else {
          // console.error('❌ [SHARED_SESSION] Erreur sync config:', error);
          toast.error(t('sharedSession.syncError'));
        }
      }
    }, debounceMs);

  }, [sessionManager, isSessionActive, isSessionRemote, debounceMs, toast]);

  // Envoyer un message de synchronisation spécifique
  const sendSyncMessage = useCallback(async (type: string, data: any) => {
    console.log(`🔍 [SHARED_SESSION] Tentative envoi message ${type}:`, {
      hasSessionManager: !!sessionManager,
      isSessionActive,
      data
    });

    if (!sessionManager || !isSessionActive) {
      // console.warn('⚠️ [SHARED_SESSION] Tentative envoi message sans session active', {
      // hasSessionManager: !!sessionManager,
      // isSessionActive
      // });
      return;
    }

    try {
      console.log(`📤 [SHARED_SESSION] Envoi message ${type}`, {
        hasSendSyncMessage: 'sendSyncMessage' in sessionManager,
        data
      });
      
      if ('sendSyncMessage' in sessionManager) {
        console.log(`🎯 [SHARED_SESSION] Utilisation sendSyncMessage directe`);
        await (sessionManager as any).sendSyncMessage(type, data);
      } else {
        console.log(`🔄 [SHARED_SESSION] Utilisation fallback syncConfig`);
        const configToSend = { [type]: data };
        console.log(`📦 [SHARED_SESSION] Config fallback à envoyer:`, configToSend);
        await sessionManager.syncConfig(configToSend);
      }
      console.log(`✅ [SHARED_SESSION] Message ${type} envoyé avec succès`);
    } catch (error) {
      // console.error(`❌ [SHARED_SESSION] Erreur envoi ${type}:`, error);
      toast.error(t("toast.sendError", { type }));
    }
  }, [sessionManager, isSessionActive, toast]);

  // Terminer la session
  const endSession = useCallback(async () => {
    if (sessionManager) {
      try {
        await sessionManager.disconnect();
        handleSessionEnd();
        toast.success(t('sharedSession.sessionEnded'));
      } catch (error) {
        // console.error('❌ [SHARED_SESSION] Erreur fin session:', error);
        toast.error(t('sharedSession.endError'));
      }
    }
  }, [sessionManager, toast]);

  const handleSessionEnd = useCallback(() => {
    setActiveSession(null);
    setSessionManager(null);
    setSyncedConfig(null);
    setConnectedDevices([]);
    setIsDeviceConnected(false);
    
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
      debounceTimeoutRef.current = null;
    }
  }, []);

  // Définir le callback de mise à jour de config
  const setConfigUpdateCallback = useCallback((callback: (config: any) => void) => {
    configUpdateCallbackRef.current = callback;
  }, []);

  // Nettoyer à la fermeture
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      
      if (sessionManager) {
        sessionManager.disconnect();
      }
    };
  }, [sessionManager]);

  return {
    // État
    activeSession,
    isSessionActive,
    isSessionServer,
    isSessionRemote,
    sessionManager,
    syncedConfig,
    connectedDevices,
    isDeviceConnected,
    
    // Actions
    updateConfig,
    sendSyncMessage,
    endSession,
    setConfigUpdateCallback,
    
    // Fonction d'initialisation (utilisée par SessionCreator)
    initializeSession: initializeSession as any
  };
}

/**
 * Hook simplifié pour les composants qui n'ont besoin que de recevoir les configs
 */
export function useSessionConfig() {
  const [config, setConfig] = useState<any>(null);
  const [isReceivingSession, setIsReceivingSession] = useState(false);

  const sharedSession = useSharedSession({
    onConfigReceived: (newConfig) => {
      setConfig(newConfig);
      setIsReceivingSession(true);
    },
    onSessionEnd: () => {
      setConfig(null);
      setIsReceivingSession(false);
    }
  });

  return {
    ...sharedSession,
    receivedConfig: config,
    isReceivingSession
  };
}

/**
 * Hook pour synchroniser automatiquement un état local avec la session
 */
export function useSyncedState<T>(
  initialValue: T,
  syncKey: string,
  sharedSession: SharedSessionHookResult
) {
  const [localValue, setLocalValue] = useState<T>(initialValue);
  const isInitializedRef = useRef(false);

  // Synchroniser les changements locaux vers la session
  useEffect(() => {
    if (isInitializedRef.current && sharedSession.isSessionRemote) {
      sharedSession.sendSyncMessage(syncKey, localValue);
    }
  }, [localValue, syncKey, sharedSession]);

  // Recevoir les changements de la session
  useEffect(() => {
    if (sharedSession.syncedConfig && sharedSession.syncedConfig[syncKey]) {
      const receivedValue = sharedSession.syncedConfig[syncKey];
      if (JSON.stringify(receivedValue) !== JSON.stringify(localValue)) {
        setLocalValue(receivedValue);
      }
    }
    isInitializedRef.current = true;
  }, [sharedSession.syncedConfig, syncKey]);

  return [localValue, setLocalValue] as const;
}


